import { ComponentStyleConfig } from "@chakra-ui/react";

const TableTheme: ComponentStyleConfig = {
  defaultProps: {
    size: "compact",
  },
  baseStyle: {
    table: {
      backgroundColor: "unset",
      borderCollapse: "separate",
      borderSpacing: 0,
      "th, td": {
        textTransform: "unset",
        letterSpacing: "unset",
        whiteSpace: "nowrap",
      },
    },
    thead: {
      zIndex: "tableThead",
      position: "sticky",
      top: 0,
      bg: "white",
      th: {
        verticalAlign: "bottom",
        color: "gray.500",
        fontWeight: 500,
        fontSize: "12px",
        borderColor: "gray.200",
        "&[data-currency-symbol]": {
          textAlign: "right",
          _after: {
            content: '"\u00a0" attr(data-currency-symbol)',
            color: "gray.400",
          },
        },
      },
    },
    tbody: {
      th: {
        textColor: "gray.700",
        fontWeight: 500,
        borderBottomWidth: 0,
      },
      td: {
        color: "gray.600",
        fontWeight: 400,
        borderWidth: 0,
        _notLast: {
          width: "unset",
        },
      },
      tr: {
        _notLast: {
          "td, th": {
            borderColor: "gray.200",
            borderBottomWidth: "1px",
          },
        },
        "&[tabindex]": {
          position: "relative",
          _hover: {
            cursor: "pointer",
            shadow: "tableHover",
            zIndex: "tableRowHover",
            "th, td": {
              bg: "gray.50",
            },
          },
          _active: {
            bg: "gray.50",
            shadow: "tableHoverActive",
            zIndex: "tableRowActive",
            "& th": {
              bg: "gray.50",
            },
          },
          _focus: {
            shadow: "tableHoverFocus",
            position: "relative",
            zIndex: "tableRowFocus",
            outline: "none !important",
          },
        },
      },
    },
  },
  variants: {
    simple: {
      table: {
        "th, td": {
          fontSize: "14px",
          px: "16px",
        },
        "th:first-of-type, td:not(* + td), td[data-index=index-0]": {
          px: "24px",
        },
        "td:last-of-type": {
          pr: "24px",
        },
      },
    },
    drawdown: {
      table: {
        borderCollapse: "separate",
        borderSpacing: "0",
        thead: {
          tr: {
            zIndex: "tableTHeadTh",
            _first: {
              position: "sticky",
              top: 0,
            },
          },
          th: {
            borderBottomWidth: "1px",
            backgroundColor: "white",
            _first: {
              position: "sticky",
              left: 0,
            },
            _last: {
              width: "99%",
            },
            "& [data-fund-title]": {
              textOverflow: "ellipsis",
              overflow: "hidden",
              whiteSpace: "pre-wrap",
            },
            "&.mb-drawdown-fund-family, &.mb-drawdown-fund": {
              minWidth: "231px",
            },
          },
        },
        "tbody, tfoot": {
          "&:not(:last-of-type) tr": {
            "th, td": {
              borderBottomWidth: 1,
              borderColor: "gray.200",
            },
          },
          "tr:first-of-type:last-of-type th": {
            height: "52px",
          },
          "tr:first-of-type .mb-drawdown-allocation": {
            pt: "48px",
          },
          "tr:not(:first-of-type):last-of-type": {
            "th, td": {
              borderTopWidth: 0,
            },
          },
          "tr:first-of-type:not(:last-of-type)": {
            "th, td": {
              borderBottomWidth: 0,
            },
          },
          "tr:first-of-type": {
            "th, td": {
              pt: "16px",
            },
          },
          "tr:last-of-type": {
            "th, td": {
              pb: "16px",
            },
          },
          "tr:not(:first-of-type)": {
            "th, td": {
              pt: "4px",
            },
          },
          "tr:not(:last-of-type)": {
            "th, td": {
              pb: "4px",
            },
          },
          "tr:not(:first-of-type):not(:last-of-type)": {
            "th, td": {
              borderTopWidth: 0,
              borderBottomWidth: 0,
            },
          },
          th: {
            verticalAlign: "middle",
            textColor: "gray.600",
            fontWeight: 500,
            borderLeftWidth: 0,
            borderRightWidth: "1px",
            position: "sticky",
            left: 0,
            zIndex: "tableTBodyTh",
            backgroundColor: "white",
            px: "24px",
            minWidth: "520px",
            height: "unset",
            ".chakra-form-control": {
              mb: 0,
            },
          },
          td: {
            px: "10px",
            borderLeftWidth: 0,
            borderRightWidth: "1px",
            verticalAlign: "bottom",
            _first: {
              borderLeftWidth: 0,
            },
            _last: {
              borderRightWidth: 0,
            },
            "&.mb-drawdown-allocation": {
              textAlign: "center",
              verticalAlign: "middle",
              "tbody tr:first-of-type &": {
                pt: "48px",
              },
            },
            "& select, & input, & button": {
              width: "200px",
            },
          },
        },
        tfoot: {
          tr: {
            _first: {
              "th, td": {
                borderTopWidth: "1px",
              },
              th: {
                paddingLeft: "48px",
              },
            },
          },
        },
      },
    },
    form: {
      thead: {
        th: {
          borderBottomWidth: "1px",
          paddingStart: "4px",
          paddingRight: "4px",
          _first: {
            "&:not(:empty)": {
              pl: "24px",
            },
          },
        },
      },
      tbody: {
        tr: {
          td: {
            paddingY: "8px",
            paddingRight: "4px",
            paddingStart: "4px",
            _first: {
              "&:not(:empty)": {
                pl: "24px",
              },
            },
            _last: {
              pr: "16px",
            },
          },
          _first: {
            td: {
              paddingTop: "10px",
            },
          },
        },
      },
      tfoot: {
        position: "sticky",
        bottom: 0,
        zIndex: 1,
        // hack to add space above the footer
        _before: {
          content: '""',
          display: "block",
          height: "8px",
        },
        tr: {
          bgColor: "gray.100",
        },
        td: {
          textStyle: "bodyBold",
          px: 0,
          py: "14px",
          borderTopWidth: "1px",
          borderTopColor: "gray.200",
          _first: {
            pl: "24px",
          },
          _notFirst: {
            "&:not(:empty)": {
              textAlign: "right",
              pr: "20px",
            },
          },
        },
      },
      tr: {
        td: {
          paddingY: "8px",
        },
      },
    },
    descriptions: {
      table: {
        width: "100%",
        borderSpacing: "8px 0",
        td: {
          whiteSpace: "normal",
        },
      },
      tbody: {
        tr: {
          _first: {
            "th, td": {
              borderTopLeftRadius: "6px",
              borderTopRightRadius: "6px",
              paddingTop: "16px",
            },
          },
          _last: {
            "th, td": {
              borderBottomLeftRadius: "6px",
              borderBottomRightRadius: "6px",
              paddingBottom: "16px",
            },
          },
          "th, td": {
            color: "gray.700",
            verticalAlign: "middle",
            paddingY: "16px",
            borderBottom: "none",
            whiteSpace: "normal",
          },
          th: {
            textStyle: "smallBold",
            paddingTop: "8px",
            paddingBottom: "8px",
            backgroundColor: "gray.100",
            minWidth: "200px",
          },
          td: {
            textStyle: "body",
            paddingTop: "8px",
            paddingBottom: "8px",
            backgroundColor: "gray.50",
          },
        },
      },
    },
    stats: {
      td: {
        textAlign: "right",
      },
    },
    lightweight: {
      table: {
        bg: "gray.100",
        borderRadius: "8px",
        width: "unset",
      },
      thead: {
        background: "transparent",
        tr: {
          th: {
            color: "gray.800",
            textStyle: "smallBold",
          },
        },
      },
      tbody: {
        tr: {
          th: {
            color: "gray.600",
            textStyle: "smallBold",
          },
          td: {
            textStyle: "small",
          },
          _notLast: {
            "th, td": {
              borderBottomWidth: 0,
            },
          },
        },
      },
    },
  },
  sizes: {
    tall: {
      th: {
        height: "56px",
        px: "16px",
        py: "8px",
      },
      td: {
        py: "12px",
        height: "72px",
      },
    },
    comfortable: {
      th: {
        height: "56px",
        px: "16px",
        py: "8px",
        _first: {
          px: "24px",
        },
      },
      td: {
        height: "56px",
      },
    },
    compact: {
      thead: {
        tr: {
          _first: {
            _last: {
              th: {
                height: "56px",
              },
            },
          },
          _notFirst: {
            th: {
              height: "43px",
            },
          },
        },
        th: {
          ps: "16px",
          pe: "16px",
          py: "8px",
          _first: {
            "&:not(:empty)": {
              ps: "24px",
            },
          },
        },
      },
      tbody: {
        "th, td": {
          ps: "16px",
        },
        th: {
          height: "43px",
        },
        td: {
          height: "43px",
        },
      },
    },
    xs: {
      tbody: {
        "th, td": {
          fontSize: "11px",
          height: "32px",
          paddingInline: "16px !important",
        },
      },
    },
    lightweight: {
      table: {
        "th, td": {
          px: "16px",
          py: "8px",
        },
      },
    },
  },
};

export default TableTheme;
